export class UserProfileTabs {
  constructor(sites, linkedUsersAndCrmOrgs, siteTypeOrder, linkedDataContext) {
    this.linkedDataContext = linkedDataContext;
    this.tabs = [];
    const crmOrgs = linkedUsersAndCrmOrgs.crm_orgs;
    crmOrgs.forEach(org => this.tabs.push(new CrmTab(org, linkedDataContext)));

    // Site tabs need the crm contact data for their profile column, let's load it now
    // Load data sequentially to avoid 429 - too-many-requests from backend
    let crmTabs = this.tabs.filter(tab => tab.isCrmTab);
    crmTabs.reduce((p, crmTab) => p.finally(() => crmTab.loadLinkedData()), Promise.resolve());

    sites.forEach(site => {
      const newTab = new SiteTab(site, linkedDataContext);
      let foundUser = linkedUsersAndCrmOrgs.users.find(user => user.site_id === site.id);
      if (foundUser) {
        newTab.setLinkedUser(foundUser);
      }
      this.tabs.push(newTab);
    });
  }

  siteTabs() {
    return this.tabs.filter(tab => !tab.isCrmTab);
  }

  crmTabs() {
    return this.tabs.filter(tab => tab.isCrmTab);
  }

  addUser(user) {
    const tab = this.siteTabs().find(tab => tab.isSiteId(user.site_id));
    if (tab) {
      tab.setLinkedUser(user);
    }
  }

  allLinkedUserIds() {
    const result = [];
    this.siteTabs().forEach(tab => {
      if (tab.hasLinkedUser()) {
        result.push(tab.getLinkedUserId());
      }
    });
    return result;
  }

  /**
   * Clears all linked users from the tabs, then sets the given linked users to the corresponding tabs.
   * Called after linking a site to a crm organisation, which could have the side effect of
   * linking it to some other already linked sites.
   */
  updateLinkedUsers(users) {
    this.siteTabs().forEach(tab => tab.removeLinkedUser());
    users.forEach(user => this.addUser(user));
  }

  /**
   * Updates the mdActive status for all tabs when the user clicks on the given tab.
   */
  onTabSelected(selectedTab) {
    this.tabs.forEach(tab => tab.onTabSelected(selectedTab));
  }

  /**
   * Updates the mdActive status for all tabs,
   * so that the site tab that has the given userId is mdActive and all others are not
   */
  activateTabForUser(userId) {
    this.crmTabs().forEach(tab => tab.onTabSelected(null));
    this.activateTabForUserOrOrganisation(userId, this.siteTabs());
  }

  /**
   * Updates the mdActive status for all tabs,
   * so that the crm tab that has the given orgId is mdActive and all others are not
   */
  activateTabForOrganisation(orgId) {
    this.activateTabForUserOrOrganisation(orgId, this.crmTabs());
    this.siteTabs().forEach(tab => tab.onTabSelected(null));
  }

  /**
   * Updates the mdActive status for all given tabs,
   * so that the tab that has the given orgId or userId is mdActive and all others are not
   */
  activateTabForUserOrOrganisation(userOrOrgId, siteOrCrmTabs) {
    let tabForUserOrOrg = null;
    siteOrCrmTabs.forEach(tab => {
      if (tab.isForLinkedEntityId(userOrOrgId)) {
        tabForUserOrOrg = tab;
      }
    });
    this.onTabSelected(tabForUserOrOrg);
  }
}

class Tab {
  constructor(linkedDataContext, tabIcon) {
    this.linkedDataContext = linkedDataContext;
    this.tabIcon = tabIcon;
    this.mdActive = false;
    this.loading = false;
    this.linkedData = undefined;
    this.isSelectable = false; // this.isCrmTab || this.hasLinkedUser();
    this.isCrmTab = false;
  }

  isActive() {
    return this.mdActive;
  }

  /**
   * Updates the mdActive status of this tab,
   * and loads the corresponding linked data if necessary
   */
  onTabSelected(selectedTab) {
    this.setActiveAndMaybeLoadLinkedData(selectedTab === this);
  }

  // Sites tab can only load data if they have a linked user
  canLoadLinkedData() {
    return true;
  }

  loadLinkedDataPromise() {
    throw 'abstract method';
  }

  isForLinkedEntityId(entityId) {
    throw 'abstract method';
  }

  setActiveAndMaybeLoadLinkedData(isActive) {
    this.mdActive = isActive;
    if (isActive && this.linkedData === undefined && !this.loading && this.canLoadLinkedData()) {
      this.loadLinkedData();
    }
  }

  loadLinkedData() {
    this.loading = true;
    return this.loadLinkedDataPromise()
      .then(linkedData => (this.linkedData = linkedData))
      .finally(() => (this.loading = false));
  }
}

class SiteTab extends Tab {
  constructor(site, linkedDataContext) {
    super(linkedDataContext, site.type_name);
    this.site = site;
    this.linkedUser = null;
  }

  isSiteId(siteId) {
    return this.site.id === siteId;
  }

  setLinkedUser(user) {
    this.linkedUser = user;
    this.isSelectable = true;
  }

  hasLinkedUser() {
    return this.linkedUser !== null;
  }

  getLinkedUserId() {
    return this.hasLinkedUser() ? this.linkedUser.user_id : null;
  }

  isForLinkedEntityId(entityId) {
    return this.hasLinkedUser() ? this.linkedUser.user_id === entityId : false;
  }

  removeLinkedUser() {
    this.linkedUser = null;
  }

  canLoadLinkedData() {
    return this.hasLinkedUser();
  }

  loadLinkedDataPromise() {
    return this.linkedDataContext.loadSiteLinkedData(this.linkedUser);
  }
}

class CrmTab extends Tab {
  constructor(organization, linkedDataContext) {
    super(linkedDataContext, organization.type);
    this.organization = organization;
    this.isSelectable = true;
    this.isCrmTab = true;

    // data will be loaded immediately after, so mark it as loading now so
    // that onSelected does not trigger additionnal loading
    this.loading = true;
  }

  isForLinkedEntityId(entityId) {
    return this.organization.id === entityId;
  }

  loadLinkedDataPromise() {
    return this.linkedDataContext.loadCrmLinkedData(this.organization);
  }
}
